import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowLeftIcon, ArrowUpIcon, FileSignatureIcon, Music2Icon } from 'lucide-react';
import { twMerge } from 'tailwind-merge';
import { OfferCard } from './OfferCard';
import { OfferSheet } from './OfferSheet';
import { BookingTicket } from './BookingTicket';
import { SongRow } from '../SongRow';
import { Button } from '../ui/Button';
import { useMarketplace } from '../../contexts/MarketplaceContext';
import { formatDate } from '../../utils/format';
import { spring } from '../../utils/motion';
import type { Role } from '../../types/marketplace';
interface ChatViewProps {
  matchId: string;
  role: Role;
  onBack?: () => void;
}
const quickReplies: Record<Role, string[]> = {
  performer: ['Share my setlist', 'Is there a sound system?', 'What’s the dress code?'],
  organiser: ['Can you do a 2-hour set?', 'Do you bring your own PA?', 'What do you need on the night?']
};
export function ChatView({
  matchId,
  role,
  onBack
}: ChatViewProps) {
  const {
    matches,
    messages,
    offers,
    typing,
    getEvent,
    getPerformer,
    sendMessage
  } = useMarketplace();
  const match = matches.find(m => m.id === matchId);
  const event = match ? getEvent(match.eventId) : undefined;
  const performer = match ? getPerformer(match.performerId) : undefined;
  const list = messages[matchId] ?? [];
  const initialCount = useRef(list.length);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [text, setText] = useState('');
  const [offerOpen, setOfferOpen] = useState(false);
  const [keysOpen, setKeysOpen] = useState(false);
  const isTyping = Boolean(typing[matchId]);
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTo({
      top: el.scrollHeight,
      behavior: initialCount.current === list.length ? 'auto' : 'smooth'
    });
  }, [list.length, isTyping]);
  if (!match || !event || !performer) return null;
  const counterpart = role === 'performer' ? {
    name: event.organiser,
    photo: event.photos[0]
  } : {
    name: performer.name,
    photo: performer.photos[0]
  };
  const hasLiveOffer = offers.some(o => o.matchId === matchId && o.status !== 'declined');
  const lastMine = list.map(m => m.from).lastIndexOf(role);
  const send = (value: string) => {
    const t = value.trim();
    if (!t) return;
    sendMessage(matchId, role, {
      text: t
    });
    setText('');
  };
  const onQuick = (q: string) => {
    if (q === 'Share my setlist') {
      send(`Setlist idea: ${performer.keys.map(k => k.title).join(', ')}.`);
    } else send(q);
  };
  return <div className="flex h-full min-h-0 flex-col" data-id="element-91" data-mp="1jy1aieb6shbm">
      <header className="flex items-center gap-3 border-b border-line px-4 py-3 lg:px-6" data-id="element-92" data-mp="1jwq5yc9x35jg">
        {onBack && <button type="button" onClick={onBack} aria-label="Back to conversations" className="grid h-10 w-10 place-items-center rounded-full text-ink-2 transition-colors duration-150 hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand lg:hidden" data-id="element-93" data-mp="1fysgr9g5cjg2">
            <ArrowLeftIcon className="h-5 w-5" data-id="element-94" data-mp="2k5pw6mbfwk8b" />
          </button>}
        <img src={counterpart.photo} alt="" className="h-10 w-10 rounded-full object-cover" data-id="element-95" data-mp="2p5kjkfw0arom" />
        <div className="min-w-0 flex-1" data-id="element-96" data-mp="2twaq3wg650kw">
          <p className="truncate font-bold leading-tight" data-id="element-97" data-mp="2txcd8zagldiz">{counterpart.name}</p>
          <p className="truncate text-[13px] text-ink-3" data-id="element-98" data-mp="2yvx5ocjgyswv">{isTyping ? 'typing…' : 'Usually replies within an hour'}</p>
        </div>
      </header>

      <div className="flex items-center gap-3 border-b border-line bg-surface-2/60 px-4 py-2.5 lg:px-6" data-id="element-99" data-mp="33snkyxng9kh8">
        <img src={event.photos[0]} alt="" className="h-10 w-10 rounded-sm object-cover" data-id="element-100" data-mp="37tzcx9tsqhsl" />
        <div className="min-w-0 flex-1" data-id="element-101" data-mp="3cudlqu381vro">
          <p className="truncate text-sm font-bold" data-id="element-102" data-mp="1diav8zw6k14i">{event.name}</p>
          <p className="truncate text-[13px] text-ink-3" data-id="element-103" data-mp="1dk5i4jr0i3b8">
            {formatDate(event.date)} · {event.venue}
          </p>
        </div>
        {role === 'organiser' && !hasLiveOffer && <Button variant="accent" size="sm" icon={<FileSignatureIcon className="h-4 w-4" data-id="element-105" data-mp="i84ilad8cx9h" />} onClick={() => setOfferOpen(true)} data-id="element-104" data-mp="n54if3jhl3s5">
            Send offer
          </Button>}
      </div>

      <div ref={scrollRef} className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-5 lg:px-6" aria-live="polite" data-id="element-106" data-mp="x4dj0f8uxgnh">
        {list.map((m, i) => {
        const isNew = i >= initialCount.current;
        const mine = m.from === role;
        const song = m.songId ? performer.keys.find(k => k.id === m.songId) : undefined;
        const offer = m.offerId ? offers.find(o => o.id === m.offerId) : undefined;
        return <motion.div key={m.id} initial={isNew ? {
          opacity: 0,
          y: 10,
          scale: 0.98
        } : false} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} transition={spring.smooth} className={twMerge('flex flex-col', m.kind === 'system' || m.kind === 'confirmed' ? 'items-center' : mine ? 'items-end' : 'items-start')} data-id="element-107" data-mp="9fu9jwe2mxtt" data-mpi={i}>
              {m.kind === 'system' && <p className="rounded-full bg-surface-2 px-3 py-1 text-center text-[13px] font-semibold text-ink-3" data-id="element-108" data-mp="1x60exbx91ewb">{m.text}</p>}
              {m.kind === 'text' && <p className={twMerge('max-w-[80%] whitespace-pre-wrap rounded-md px-4 py-2.5 text-[15px] leading-relaxed', mine ? 'rounded-br-sm bg-ink text-bg' : 'rounded-bl-sm bg-surface-2 text-ink')} data-id="element-109" data-mp="1nedxgxubijl4">
                  {m.text}
                </p>}
              {m.kind === 'song' && song && <div className={twMerge('w-[280px] max-w-[80%] rounded-md border border-line bg-surface p-3', mine ? 'rounded-br-sm' : 'rounded-bl-sm')} data-id="element-110" data-mp="264lwp4uwcpw1">
                  <p className="mb-2 text-xs font-bold uppercase tracking-[0.06em] text-ink-3" data-id="element-111" data-mp="qr97epdmevtb">From their Keys</p>
                  <SongRow song={song} data-id="element-112" data-mp="2xcvuml6dawbx" />
                </div>}
              {m.kind === 'offer' && offer && <OfferCard offer={offer} role={role} data-id="element-113" data-mp="2sdiwh4a1q2u1" />}
              {m.kind === 'confirmed' && offer && <BookingTicket offer={offer} tear={isNew} data-id="element-114" data-mp="2nn2jtme1ul8q" />}
              {i === lastMine && m.kind !== 'offer' && <span className="mt-1 text-[11px] font-semibold text-ink-3" data-id="element-115" data-mp="2ip10d0cpojwq">{i < list.length - 1 || isTyping ? 'Read' : 'Delivered'}</span>}
            </motion.div>;
      })}
        <AnimatePresence data-id="element-116" data-mp="29nb85sb09gxw">
          {isTyping && <motion.div initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0
        }} transition={spring.smooth} className="flex w-fit items-center gap-1 rounded-md rounded-bl-sm bg-surface-2 px-4 py-3" aria-label={`${counterpart.name} is typing`} data-id="element-117" data-mp="29pnlfs5tj34m">
              {[0, 1, 2].map(d => <motion.span key={d} className="h-2 w-2 rounded-full bg-ink-3" animate={{
            y: [0, -4, 0]
          }} transition={{
            duration: 0.6,
            repeat: Infinity,
            delay: d * 0.12,
            ease: 'easeInOut'
          }} data-id="element-118" data-mp="3u1l87zayp2s3" />)}
            </motion.div>}
        </AnimatePresence>
      </div>

      <div className="border-t border-line px-4 pb-3 pt-2.5 lg:px-6" data-id="element-119" data-mp="k5xgu6e8h4k0">
        <div className="no-scrollbar -mx-1 flex gap-2 overflow-x-auto px-1 pb-2.5" data-id="element-120" data-mp="3jmm3vtbv1cvr">
          {quickReplies[role].map(q => <motion.button key={q} type="button" whileTap={{
          scale: 0.95
        }} transition={spring.snappy} onClick={() => onQuick(q)} className="h-8 shrink-0 whitespace-nowrap rounded-full border border-line px-3 text-[13px] font-bold text-ink-2 transition-colors duration-150 hover:border-ink-3 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand" data-id="element-121" data-mp="3nnfs6nt7stp6">
              {q}
            </motion.button>)}
        </div>
        <div className="relative" data-id="element-122" data-mp="1ty3jc3bvp7xj">
          <AnimatePresence data-id="element-123" data-mp="1tz4vkb4ms0gu">
            {keysOpen && <motion.div initial={{
            opacity: 0,
            y: 8,
            scale: 0.98
          }} animate={{
            opacity: 1,
            y: 0,
            scale: 1
          }} exit={{
            opacity: 0,
            y: 8,
            scale: 0.98
          }} transition={{
            duration: 0.16,
            ease: 'easeOut'
          }} className="absolute bottom-full left-0 z-10 mb-2 w-[300px] rounded-md border border-line bg-surface p-2 shadow-xl" data-id="element-124" data-mp="27oovzaaqolmz">
                <p className="px-2 pb-1 pt-1 text-xs font-bold uppercase tracking-[0.06em] text-ink-3" data-id="element-125" data-mp="2qo2qpugam0ej">Share a song</p>
                {performer.keys.map(k => <button key={k.id} type="button" onClick={() => {
              sendMessage(matchId, role, {
                songId: k.id
              });
              setKeysOpen(false);
            }} className="flex w-full items-center justify-between rounded-sm px-2 py-2 text-left transition-colors duration-150 hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand" data-id="element-126" data-mp="w9tfrjwg2c4b">
                    <span className="min-w-0" data-id="element-127" data-mp="2y73n7vsoetse">
                      <span className="block truncate text-sm font-bold" data-id="element-128" data-mp="2y60urjfpgjad">{k.title}</span>
                      <span className="block truncate text-xs text-ink-3" data-id="element-129" data-mp="2k6j757e5bc2l">{k.artist}</span>
                    </span>
                  </button>)}
              </motion.div>}
          </AnimatePresence>
          <form className="flex items-center gap-2" onSubmit={e => {
          e.preventDefault();
          send(text);
        }} data-id="element-130" data-mp="2b8p83oozy4bq">
            {role === 'performer' && <button type="button" onClick={() => setKeysOpen(o => !o)} aria-label="Share a song from your Keys" aria-expanded={keysOpen} className={twMerge('grid h-11 w-11 shrink-0 place-items-center rounded-full transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand', keysOpen ? 'bg-ink text-bg' : 'bg-surface-2 text-ink-2 hover:text-ink')} data-id="element-131" data-mp="dh4h7rv6yusf">
                <Music2Icon className="h-5 w-5" data-id="element-132" data-mp="2urk8s1itkh90" />
              </button>}
            <input value={text} onChange={e => setText(e.target.value)} placeholder="Message" aria-label="Message" className="h-11 min-w-0 flex-1 rounded-full border border-line bg-surface px-4 text-[15px] placeholder:text-ink-3 focus:border-ink focus:outline-none" data-id="element-133" data-mp="2zplsmoo2kklj" />
            <motion.button type="submit" aria-label="Send" disabled={!text.trim()} animate={{
            scale: text.trim() ? 1 : 0.9
          }} whileTap={{
            scale: 0.85
          }} transition={spring.bouncy} className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-accent text-on-accent transition-opacity duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand disabled:opacity-40" data-id="element-134" data-mp="1acs1y3ykqysn">
              <ArrowUpIcon className="h-5 w-5" strokeWidth={2.5} data-id="element-135" data-mp="o204dpn21z95" />
            </motion.button>
          </form>
        </div>
      </div>

      {role === 'organiser' && <OfferSheet open={offerOpen} onClose={() => setOfferOpen(false)} matchId={matchId} data-id="element-136" data-mp="j4gq4pqcxmh7" />}
    </div>;
}